import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useState, useCallback, useRef } from 'react'
import { EventSourcePolyfill } from 'event-source-polyfill'
import { useAutomationClient } from '../providers/AutomationProvider'
import type { ActivityListParams, ActivityEvent, ActivityListResponse } from '../types'

/**
 * Query key factory for activity-related queries
 */
export const activityKeys = {
  all: ['activity'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (params?: ActivityListParams) => [...activityKeys.lists(), params] as const,
  infinite: (params?: Omit<ActivityListParams, 'cursor'>) => [...activityKeys.all, 'infinite', params] as const,
}

/**
 * Hook to fetch a paginated list of activity events
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useActivity({ limit: 50, automationId: 'abc123' })
 * console.log(data?.activities)
 * ```
 */
export function useActivity(params?: ActivityListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () => client.activity.list(params),
  })
}

export type InfiniteActivityParams = Omit<ActivityListParams, 'cursor' | 'offset'>

/**
 * Hook to fetch infinite/paginated activity events using cursor-based pagination
 * Perfect for infinite scroll implementations with virtualization
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 * } = useInfiniteActivity({ limit: 30 })
 *
 * // Flatten all pages into a single array
 * const allEvents = data?.pages.flatMap(page => page.activities) ?? []
 * ```
 */
export function useInfiniteActivity(params?: InfiniteActivityParams) {
  const { client } = useAutomationClient()

  return useInfiniteQuery({
    queryKey: activityKeys.infinite(params),
    queryFn: ({ pageParam }) =>
      client.activity.list({
        ...params,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ActivityListResponse) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
  })
}

interface UseActivityStreamOptions {
  enabled?: boolean
  onEvent?: (event: ActivityEvent) => void
  onError?: (error: Error) => void
}

/**
 * Hook to subscribe to real-time activity events via SSE
 *
 * @example
 * ```tsx
 * const { events, isConnected, error } = useActivityStream({
 *   enabled: true,
 *   onEvent: (event) => console.log('New event:', event),
 * })
 * ```
 */
export function useActivityStream(options: UseActivityStreamOptions = {}) {
  const { enabled = true, onEvent, onError } = options
  const { baseUrl } = useAutomationClient()

  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Use refs for callbacks to avoid reconnecting when they change
  const onEventRef = useRef(onEvent)
  const onErrorRef = useRef(onError)

  // Update refs when callbacks change
  useEffect(() => {
    onEventRef.current = onEvent
  }, [onEvent])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const clearEvents = useCallback(() => {
    setEvents([])
  }, [])

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false)
      return
    }

    let eventSource: EventSourcePolyfill | null = null
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
    let isMounted = true

    const connect = () => {
      if (!isMounted) return

      try {
        const url = baseUrl || ''
        // Use EventSourcePolyfill to support credentials for cross-origin requests
        eventSource = new EventSourcePolyfill(`${url}/api/activity/stream`, {
          withCredentials: true,
        })

        eventSource.onopen = () => {
          if (isMounted) {
            setIsConnected(true)
            setError(null)
          }
        }

        eventSource.onmessage = (event) => {
          if (!isMounted) return

          try {
            const data = JSON.parse(event.data)

            // Skip connection events
            if (data.type === 'connected') {
              return
            }

            const activityEvent = data as ActivityEvent
            setEvents((prev) => [activityEvent, ...prev].slice(0, 100)) // Keep last 100 events
            onEventRef.current?.(activityEvent)
          } catch (parseError) {
            console.error('Failed to parse SSE event:', parseError)
          }
        }

        eventSource.onerror = () => {
          if (!isMounted) return

          setIsConnected(false)
          const err = new Error('SSE connection error')
          setError(err)
          onErrorRef.current?.(err)

          // Close the errored connection
          eventSource?.close()
          eventSource = null

          // Attempt to reconnect after a delay
          reconnectTimeout = setTimeout(() => {
            if (isMounted && enabled) {
              connect()
            }
          }, 5000)
        }
      } catch (err) {
        if (!isMounted) return
        const error = err instanceof Error ? err : new Error('Failed to connect to SSE')
        setError(error)
        onErrorRef.current?.(error)
      }
    }

    connect()

    return () => {
      isMounted = false
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      eventSource?.close()
      setIsConnected(false)
    }
  }, [enabled, baseUrl]) // Only depend on enabled and baseUrl, not callbacks

  return {
    events,
    isConnected,
    error,
    clearEvents,
  }
}
